import { useEffect, useState } from 'react'
import { useAuth } from '../auth/useAuth'
import { getBrowserNotificationStatus, shouldShowDeviceNotificationOnboarding } from './notificationPermission'
import { attachForegroundNotificationClick, registerFcmDeviceToken, requestAdminDailyOpenSummary, subscribeToForegroundFcmMessages } from './fcmClient'
import { getStableDeviceId } from './deviceIdentity'
import { getOwnNotificationToken, saveOwnNotificationToken } from '../../services/firestore/notificationTokenService'
import type { NotificationStatusValue, PushTokenStatus } from '../../services/firestore/firestoreTypes'

function browserStatus(): NotificationStatusValue {
  return getBrowserNotificationStatus(typeof window === 'undefined' || !('Notification' in window) ? undefined : Notification)
}

function deviceMetadata() {
  const userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent || ''
  return {
    browser: userAgent.includes('Edg/') ? 'Edge'
      : userAgent.includes('Chrome/') ? 'Chrome'
        : userAgent.includes('Firefox/') ? 'Firefox'
          : userAgent.includes('Safari/') && !userAgent.includes('Chrome/') ? 'Safari' : 'Unknown browser',
    platform: typeof navigator === 'undefined' ? '' : navigator.platform || '',
    userAgent,
  }
}

export function NotificationOnboarding() {
  const { user, role, schoolId, loading } = useAuth()
  const userUid = user?.uid
  const [deviceId] = useState(() => getStableDeviceId())
  const [visible, setVisible] = useState(false)
  const [saving, setSaving] = useState(false)
  const [catchUpCount, setCatchUpCount] = useState<number | null>(null)
  const [catchUpRetryPending, setCatchUpRetryPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const eligibleForReminder = !loading && Boolean(user) && (role === 'admin' || role === 'parent' || role === 'employee') && Boolean(schoolId)

  useEffect(() => {
    if (loading || !userUid || !role || !schoolId || (role !== 'admin' && role !== 'parent' && role !== 'employee')) return
    const scopedUid = userUid
    const scopedRole = role
    let active = true
    async function loadStatus() {
      setError(null)
      try {
        const current = await getOwnNotificationToken(scopedUid, deviceId)
        if (!active) return
        const currentBrowserStatus = browserStatus()
        setVisible(shouldShowDeviceNotificationOnboarding({
          role: scopedRole,
          deviceStatus: current?.onboardingStatus,
          browserStatus: currentBrowserStatus,
        }))
      } catch (loadError) {
        if (active) {
          setVisible(true)
          setError(loadError instanceof Error ? loadError.message : 'Notification status could not be checked. Please retry notifications.')
        }
      }
    }
    void loadStatus()
    return () => { active = false }
  }, [deviceId, loading, role, schoolId, userUid])

  useEffect(() => {
    if (!eligibleForReminder || browserStatus() !== 'enabled') return
    let unsubscribe: (() => void) | undefined
    let active = true
    void subscribeToForegroundFcmMessages((payload) => {
      if (!active || !('Notification' in window) || Notification.permission !== 'granted') return
      const title = payload.notification?.title || 'SGS Fee Portal'
      const body = payload.notification?.body || 'You have a new school fee portal update.'
      const notification = new Notification(title, {
        body,
        icon: '/icons/icon.svg',
        tag: payload.messageId || 'sgs-fee-portal-foreground',
        data: { url: payload.data?.url || '/admin/reminders?view=today' },
      })
      attachForegroundNotificationClick(notification, payload.data?.url || '/admin/reminders?view=today')
    }).then((nextUnsubscribe) => {
      if (active) unsubscribe = nextUnsubscribe
      else nextUnsubscribe()
    }).catch((foregroundError) => {
      if (!active) return
      // FCM foreground subscription failure must not reopen the permission modal.
      // The device may already have a valid token; registration and portal access remain intact.
      console.warn('[notifications] Foreground notification subscription unavailable.', foregroundError)
    })
    return () => {
      active = false
      unsubscribe?.()
    }
  }, [eligibleForReminder])

  async function saveDeviceState(onboardingStatus: NotificationStatusValue, status: PushTokenStatus = 'error', errorMessage = '') {
    if (!user || !role || !schoolId) return
    await saveOwnNotificationToken({
      deviceId,
      uid: user.uid,
      schoolId,
      role,
      token: '',
      status,
      onboardingStatus,
      ...deviceMetadata(),
      errorMessage,
    })
  }

  async function enableNotifications() {
    if (!user || !role || !schoolId) return
    setSaving(true)
    setError(null)
    try {
      const current = browserStatus()
      if (current === 'unsupported') {
        setVisible(false)
        await saveDeviceState('unsupported')
        return
      }
      if (current === 'denied') {
        setVisible(false)
        await saveDeviceState('denied')
        return
      }
      if (current !== 'enabled') {
        const permission = await Notification.requestPermission()
        if (permission !== 'granted') {
          setVisible(false)
          await saveDeviceState(permission === 'denied' ? 'denied' : 'skipped')
          return
        }
      }
      const tokenResult = await registerFcmDeviceToken({ uid: user.uid, schoolId, role })
      if (!tokenResult.ok) setError(tokenResult.reason)
      const catchUpNeedsRetry = tokenResult.ok && role === 'admin' && Boolean(tokenResult.catchUp && !tokenResult.catchUp.ok)
      if (tokenResult.ok && role === 'admin' && tokenResult.catchUp?.ok && typeof tokenResult.catchUp.remainingCount === 'number') {
        setCatchUpCount(tokenResult.catchUp.remainingCount)
        setCatchUpRetryPending(false)
      } else if (catchUpNeedsRetry) {
        setCatchUpRetryPending(true)
        setError('Notifications are enabled, but today’s reminder check could not finish. Retry it now.')
      }
      setVisible(!tokenResult.ok || catchUpNeedsRetry)
    } catch (permissionError) {
      setError(permissionError instanceof Error ? permissionError.message : 'Browser notification permission could not be requested.')
    } finally {
      setSaving(false)
    }
  }

  async function retryCatchUp() {
    if (!user || role !== 'admin') return
    setSaving(true)
    setError(null)
    try {
      const result = await requestAdminDailyOpenSummary(deviceId)
      if (result.ok) {
        setCatchUpCount(result.remainingCount ?? 0)
        setCatchUpRetryPending(false)
        setVisible(false)
      } else {
        setError('Today’s reminder check is still unavailable. You can retry again.')
      }
    } finally {
      setSaving(false)
    }
  }

  async function skipNotifications() {
    try {
      setVisible(false)
      await saveDeviceState('skipped')
    } catch (skipError) {
      setError(skipError instanceof Error ? skipError.message : 'Notification preference could not be saved.')
    }
  }

  if (!eligibleForReminder || !visible) return null

  return <div className="notification-overlay" role="presentation">
    <section className="notification-modal" role="dialog" aria-modal="true" aria-labelledby="notification-title">
      <p className="eyebrow">Notifications</p>
      <h2>{catchUpRetryPending ? 'Reminder check pending' : 'Enable fee reminders?'}</h2>
      <p id="notification-title">{catchUpRetryPending
        ? 'Notifications are already enabled on this device. Retry the secure check for today’s unresolved reminders.'
        : 'Browser notifications are optional. In-app reminders remain available if notifications are denied or skipped.'}</p>
      {role === 'admin' && <p className="muted">After enabling, today’s unresolved reminder count will be checked for catch-up readiness.</p>}
      {catchUpCount !== null && <p className="muted" role="status">{catchUpCount} unresolved reminder{catchUpCount === 1 ? '' : 's'} found for today.</p>}
      {error && <p className="error-message" role="alert">{error}</p>}
      <div className="modal-actions">
        <button className="button" type="button" disabled={saving} onClick={() => void (catchUpRetryPending ? retryCatchUp() : enableNotifications())}>{saving ? 'Saving...' : catchUpRetryPending ? 'Retry reminder check' : error ? 'Retry Notifications' : 'Enable Notifications'}</button>
        <button className="button button-secondary" type="button" disabled={saving} onClick={() => {
          if (catchUpRetryPending) setVisible(false)
          else void skipNotifications()
        }}>{catchUpRetryPending ? 'Close' : 'Not now'}</button>
      </div>
    </section>
  </div>
}